import React, { useState, useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  FaChevronLeft,
  FaChevronRight,
  FaExpandAlt,
  FaCompressAlt,
  FaBuilding,
  FaCalendarAlt,
  FaCalendarDay,
  FaCalendarCheck,
  FaCheckCircle,
  FaClock,
  FaTimesCircle,
  FaInfoCircle,
  FaSearch,
  FaTimes,
  FaChevronDown,
  FaChevronUp,
  FaChartBar,
  FaPlusSquare,
  FaProjectDiagram,
  FaPrint,
  FaTh,
  FaCheck,
  FaFilter,
} from "react-icons/fa";
import { fetchPartyCallDetailById } from "../../../Redux/Slice/Partycalldetails";
import CalendarCallCard from "./CalendarCallCard";
import DateNavigationTaskCard from "./DateNavigationTaskCard";
import CallDetailModal from "./CallDetailModal";
import PartyCallPrintReport from "./PartyCallPrintReport";
import PartyCallAnnualSummaryReport from "./PartyCallAnnualSummaryReport";
import PrintPreviewModal from "./PrintPreviewModal";
import PrintChoiceModal from "./PrintChoiceModal";
import { printCalendarReport } from "./printCalendarReport";
import Loader from "../../../Components/Loader/Loader";

// Helper to extract clean initials from company name (e.g. "AAISWARYA DYEING MILLS" -> "AD")
const getPartyInitials = (name) => {
  if (!name) return "CO";
  const cleaned = name.replace(/PVT\.?|LTD\.?|\(S\/F\)|INC\.?/gi, "").trim();
  const words = cleaned.split(/\s+/).filter(Boolean);
  if (words.length === 0) return name.slice(0, 2).toUpperCase();
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
};

// Helper for industry/type badge styling
const getTypeBadgeStyle = (type) => {
  const t = (type || "").toUpperCase();
  if (t.includes("PROCESS")) {
    return "bg-sky-50 text-sky-700 border-sky-200/90";
  }
  if (t.includes("TRADING")) {
    return "bg-purple-50 text-purple-700 border-purple-200/90";
  }
  if (t.includes("WEAVING")) {
    return "bg-amber-50 text-amber-700 border-amber-200/90";
  }
  return "bg-slate-100 text-slate-700 border-slate-200";
};

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const DAYS_OF_WEEK = [
  { name: "Sunday", short: "Sun", isWeekend: true },
  { name: "Monday", short: "Mon", isWeekend: false },
  { name: "Tuesday", short: "Tue", isWeekend: false },
  { name: "Wednesday", short: "Wed", isWeekend: false },
  { name: "Thursday", short: "Thu", isWeekend: false },
  { name: "Friday", short: "Fri", isWeekend: false },
  { name: "Saturday", short: "Sat", isWeekend: false },
];

const PartyCallCalendarView = ({ parties = [] }) => {
  const dispatch = useDispatch();
  const { selectedPartyCallDetail, partyCallDetailLoading } = useSelector(
    (state) => state.partyCall
  );

  const [selectedParty, setSelectedParty] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedProjectId, setSelectedProjectId] = useState("ALL");
  const [isExpandedAll, setIsExpandedAll] = useState(false);
  const [selectedCall, setSelectedCall] = useState(null);
  const [isPrintPreviewOpen, setIsPrintPreviewOpen] = useState(false);
  const [isChoiceModalOpen, setIsChoiceModalOpen] = useState(false);
  const [selectedReportType, setSelectedReportType] = useState("yearly-calendar");
  const [printIncludeHistory, setPrintIncludeHistory] = useState(true);
  const [printIncludeImages, setPrintIncludeImages] = useState(true);
  const [viewMode, setViewMode] = useState("date-nav"); // "date-nav" (default date-focused view) | "month-grid" (7-day grid)
  const [selectedTaskDateKey, setSelectedTaskDateKey] = useState(null);
  const [scrollerMonthFilter, setScrollerMonthFilter] = useState("ALL");
  const dateScrollerRef = useRef(null);

  const handleOpenPrintChoice = () => {
    setIsPrintPreviewOpen(false);
    setIsChoiceModalOpen(true);
  };

  const handleSelectReportType = (type, options = {}) => {
    setSelectedReportType(type);
    if (typeof options?.includeHistory === "boolean") {
      setPrintIncludeHistory(options.includeHistory);
    }
    if (typeof options?.includeImages === "boolean") {
      setPrintIncludeImages(options.includeImages);
    }
    setIsChoiceModalOpen(false);
    setIsPrintPreviewOpen(true);
  };

  // Generous column width state: default 240px per day
  const [columnWidth, setColumnWidth] = useState(() => {
    if (typeof window !== "undefined") {
      const saved = localStorage.getItem("calendar_col_width");
      if (saved) return Number(saved);
    }
    return 240;
  });

  const handleSetColumnWidth = (w) => {
    setColumnWidth(w);
    try {
      localStorage.setItem("calendar_col_width", w);
    } catch (e) { }
  };

  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  const [partySearchTerm, setPartySearchTerm] = useState("");
  const [isPartyDropdownOpen, setIsPartyDropdownOpen] = useState(false);
  const partyDropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  // Horizontal scroll controller for calendar grid
  const calendarScrollRef = useRef(null);

  const scrollByAmount = (direction) => {
    if (calendarScrollRef.current) {
      const amount = direction * columnWidth;
      calendarScrollRef.current.scrollBy({ left: amount, behavior: "smooth" });
    }
  };

  const scrollToDayColumn = (dayIndex) => {
    if (calendarScrollRef.current) {
      const scrollContainer = calendarScrollRef.current;
      const targetLeft = Math.max(0, columnWidth * dayIndex - 10);
      scrollContainer.scrollTo({ left: targetLeft, behavior: "smooth" });
    }
  };

  // Always reset horizontal scroll to Sunday (0) when month, year, or party changes
  useEffect(() => {
    if (calendarScrollRef.current) {
      calendarScrollRef.current.scrollLeft = 0;
    }
  }, [selectedMonth, selectedYear, selectedParty]);

  // Close party dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        partyDropdownRef.current &&
        !partyDropdownRef.current.contains(event.target)
      ) {
        setIsPartyDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Auto focus search input when party dropdown opens
  useEffect(() => {
    if (isPartyDropdownOpen && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 50);
    }
  }, [isPartyDropdownOpen]);

  // Track if any empty week is manually expanded
  const [expandedEmptyWeeks, setExpandedEmptyWeeks] = useState({});

  // Reset expanded empty weeks when party, month, or year changes
  useEffect(() => {
    setExpandedEmptyWeeks({});
  }, [selectedParty, selectedMonth, selectedYear]);

  // Reset selected project when party changes
  useEffect(() => {
    setSelectedProjectId("ALL");
  }, [selectedParty]);

  // Sort and filter parties by search term
  const filteredParties = useMemo(() => {
    const list = [...parties].sort((a, b) => {
      const nameA = a.CompanyName || "";
      const nameB = b.CompanyName || "";
      return nameA.localeCompare(nameB, undefined, { sensitivity: "base" });
    });

    if (!partySearchTerm.trim()) return list;
    const term = partySearchTerm.toLowerCase();
    return list.filter(
      (p) =>
        (p.CompanyName && p.CompanyName.toLowerCase().includes(term)) ||
        (p.Type && p.Type.toLowerCase().includes(term))
    );
  }, [parties, partySearchTerm]);

  // Enhanced grid state: Category filter & Sort
  const [gridCategory, setGridCategory] = useState("ALL");
  const [gridSortBy, setGridSortBy] = useState("name"); // "name" | "total" | "pending"

  // Calculate dynamic category counts for grid selection
  const partyTypeCounts = useMemo(() => {
    const counts = { ALL: parties.length, PENDING_ONLY: 0 };
    parties.forEach((p) => {
      const t = (p.Type || "").trim().toUpperCase() || "OTHER";
      counts[t] = (counts[t] || 0) + 1;
      if ((p.pending || 0) > 0 || p.Priority?.toLowerCase() === "on") {
        counts.PENDING_ONLY = (counts.PENDING_ONLY || 0) + 1;
      }
    });
    return counts;
  }, [parties]);

  const availableCategories = useMemo(() => {
    const types = Object.keys(partyTypeCounts).filter(
      (k) => k !== "ALL" && k !== "PENDING_ONLY"
    );
    return ["ALL", ...types, "PENDING_ONLY"];
  }, [partyTypeCounts]);

  const displayedParties = useMemo(() => {
    let list = [...parties];

    // Search filter
    if (partySearchTerm.trim()) {
      const term = partySearchTerm.toLowerCase();
      list = list.filter(
        (p) =>
          (p.CompanyName && p.CompanyName.toLowerCase().includes(term)) ||
          (p.Type && p.Type.toLowerCase().includes(term))
      );
    }

    // Category filter
    if (gridCategory === "PENDING_ONLY") {
      list = list.filter(
        (p) => (p.pending || 0) > 0 || p.Priority?.toLowerCase() === "on"
      );
    } else if (gridCategory !== "ALL") {
      list = list.filter(
        (p) => ((p.Type || "").trim().toUpperCase() || "OTHER") === gridCategory
      );
    }

    // Sort
    list.sort((a, b) => {
      if (gridSortBy === "total") {
        return (b.total || 0) - (a.total || 0);
      }
      if (gridSortBy === "pending") {
        return (b.pending || 0) - (a.pending || 0);
      }
      const nameA = a.CompanyName || "";
      const nameB = b.CompanyName || "";
      return nameA.localeCompare(nameB, undefined, { sensitivity: "base" });
    });

    return list;
  }, [parties, partySearchTerm, gridCategory, gridSortBy]);

  // Fetch calls when party is selected
  const handleSelectParty = async (party) => {
    if (!party) return;
    setSelectedParty(party);
    setSelectedProjectId("ALL");
    setIsPartyDropdownOpen(false);
    setIsFilterPanelOpen(false);

    const partyId = party._id || party.CompanyId;
    if (partyId) {
      try {
        await dispatch(fetchPartyCallDetailById(partyId)).unwrap();
      } catch (err) {
        console.error("Error fetching calls for party:", err);
      }
    }
  };

  // Extract all calls from loaded data
  const allCalls = useMemo(() => {
    if (!selectedPartyCallDetail) return [];
    if (Array.isArray(selectedPartyCallDetail.Total)) {
      return selectedPartyCallDetail.Total;
    }
    return [];
  }, [selectedPartyCallDetail]);

  // Extract unique projects belonging to the selected party's calls
  const { partyProjects, noProjectCount } = useMemo(() => {
    if (!allCalls || !allCalls.length) {
      return { partyProjects: [], noProjectCount: 0 };
    }
    const map = {};
    let noProj = 0;
    allCalls.forEach((c) => {
      const p = c.Project;
      const pId =
        p?._id || (typeof p === "string" && p.trim() !== "" ? p : null);
      const pName =
        p?.project_name ||
        p?.ProjectName ||
        (typeof p === "string" && p.trim() !== "" ? p : null);

      if (pId && pName) {
        const idKey = String(pId);
        if (!map[idKey]) {
          map[idKey] = {
            id: idKey,
            name: pName,
            count: 0,
          };
        }
        map[idKey].count++;
      } else {
        noProj++;
      }
    });

    const list = Object.values(map).sort((a, b) => {
      if (b.count !== a.count) return b.count - a.count;
      return a.name.localeCompare(b.name, undefined, { sensitivity: "base" });
    });

    return { partyProjects: list, noProjectCount: noProj };
  }, [allCalls]);

  // Filter allCalls by selected project
  const filteredCallsByProject = useMemo(() => {
    if (!allCalls || !allCalls.length) return [];
    if (selectedProjectId === "ALL") return allCalls;
    if (selectedProjectId === "NO_PROJECT") {
      return allCalls.filter((c) => {
        const p = c.Project;
        const pId =
          p?._id || (typeof p === "string" && p.trim() !== "" ? p : null);
        return !pId;
      });
    }
    return allCalls.filter((c) => {
      const p = c.Project;
      const pId = p?._id || (typeof p === "string" ? p : null);
      return String(pId) === String(selectedProjectId);
    });
  }, [allCalls, selectedProjectId]);

  // Compute active periods (excluding any future months beyond current month & year)
  const activePeriods = useMemo(() => {
    if (!filteredCallsByProject || !filteredCallsByProject.length) return [];
    const map = {};
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();

    filteredCallsByProject.forEach((c) => {
      if (!c.Date) return;
      const d = new Date(c.Date);
      if (isNaN(d.getTime())) return;
      const y = d.getFullYear();
      const m = d.getMonth();

      // Do not include future months beyond current month & year (e.g. if current is Sep 2026, Oct 2026 must NOT appear)
      if (y > currentYear || (y === currentYear && m > currentMonth)) return;

      const key = `${y}-${m}`;

      if (!map[key]) {
        map[key] = {
          key,
          year: y,
          month: m,
          monthName: MONTH_NAMES[m],
          count: 0,
          completed: 0,
          pending: 0,
          latestDate: d,
        };
      }

      map[key].count++;
      const s = (c.Status || "").toLowerCase();
      if (s === "completed") map[key].completed++;
      else if (s === "pending") map[key].pending++;

      if (d > map[key].latestDate) {
        map[key].latestDate = d;
      }
    });

    return Object.values(map).sort((a, b) => {
      if (a.year !== b.year) return b.year - a.year;
      return b.month - a.month;
    });
  }, [filteredCallsByProject]);

  // Available years
  const availableYears = useMemo(() => {
    if (!activePeriods.length) {
      return [{ year: new Date().getFullYear(), count: 0 }];
    }
    const yearMap = {};
    activePeriods.forEach((p) => {
      yearMap[p.year] = (yearMap[p.year] || 0) + p.count;
    });

    return Object.keys(yearMap)
      .map(Number)
      .sort((a, b) => b - a)
      .map((y) => ({
        year: y,
        count: yearMap[y],
      }));
  }, [activePeriods]);

  // Month counts for current year
  const monthCountsForYear = useMemo(() => {
    const counts = {};
    for (let i = 0; i < 12; i++) {
      counts[i] = { total: 0, completed: 0, pending: 0 };
    }
    if (!filteredCallsByProject || !filteredCallsByProject.length) return counts;

    filteredCallsByProject.forEach((c) => {
      if (!c.Date) return;
      const d = new Date(c.Date);
      if (isNaN(d.getTime())) return;
      if (d.getFullYear() === selectedYear) {
        const m = d.getMonth();
        counts[m].total++;
        const s = (c.Status || "").toLowerCase();
        if (s === "completed") counts[m].completed++;
        else if (s === "pending") counts[m].pending++;
      }
    });

    return counts;
  }, [filteredCallsByProject, selectedYear]);

  // Full Year 12-Month Annual Summary Data
  const annualSummaryData = useMemo(() => {
    const months = [];
    let yearTotal = 0;
    let yearCompleted = 0;
    let yearPending = 0;
    let yearOther = 0;

    for (let m = 0; m < 12; m++) {
      months.push({
        monthIndex: m,
        monthName: MONTH_NAMES[m],
        shortName: MONTH_NAMES[m].slice(0, 3),
        total: 0,
        completed: 0,
        pending: 0,
        other: 0,
        resolutionRate: "100%",
      });
    }

    if (filteredCallsByProject && filteredCallsByProject.length) {
      filteredCallsByProject.forEach((c) => {
        if (!c.Date) return;
        const d = new Date(c.Date);
        if (isNaN(d.getTime())) return;
        if (d.getFullYear() === selectedYear) {
          const m = d.getMonth();
          months[m].total++;
          yearTotal++;

          const s = (c.Status || "").toLowerCase();
          if (s === "completed") {
            months[m].completed++;
            yearCompleted++;
          } else if (s === "pending") {
            months[m].pending++;
            yearPending++;
          } else {
            months[m].other++;
            yearOther++;
          }
        }
      });
    }

    months.forEach((m) => {
      m.resolutionRate =
        m.total > 0
          ? `${Math.round((m.completed / m.total) * 100)}%`
          : "-";
    });

    const yearResolutionRate =
      yearTotal > 0
        ? `${Math.round((yearCompleted / yearTotal) * 100)}%`
        : "100%";

    return {
      year: selectedYear,
      months,
      totals: {
        total: yearTotal,
        completed: yearCompleted,
        pending: yearPending,
        other: yearOther,
        resolutionRate: yearResolutionRate,
      },
    };
  }, [filteredCallsByProject, selectedYear]);

  // Smart Auto-Selection of latest month with calls
  const prevPartyIdRef = useRef(null);
  useEffect(() => {
    if (!selectedParty || filteredCallsByProject.length === 0) return;
    const currentPartyId = selectedParty._id || selectedParty.CompanyId;
    const isPartyChanged = prevPartyIdRef.current !== currentPartyId;
    prevPartyIdRef.current = currentPartyId;

    if (isPartyChanged && activePeriods.length > 0) {
      const hasCallsNow = filteredCallsByProject.some((c) => {
        if (!c.Date) return false;
        const d = new Date(c.Date);
        return (
          d.getFullYear() === selectedYear && d.getMonth() === selectedMonth
        );
      });

      if (!hasCallsNow) {
        const latest = activePeriods[0];
        setSelectedYear(latest.year);
        setSelectedMonth(latest.month);
      }
    }
  }, [selectedParty, filteredCallsByProject, selectedYear, selectedMonth, activePeriods]);

  // Auto-switch month when project filter changes
  const prevProjectIdRef = useRef(selectedProjectId);
  useEffect(() => {
    if (prevProjectIdRef.current !== selectedProjectId) {
      prevProjectIdRef.current = selectedProjectId;
      if (filteredCallsByProject.length > 0 && activePeriods.length > 0) {
        const hasCallsNow = filteredCallsByProject.some((c) => {
          if (!c.Date) return false;
          const d = new Date(c.Date);
          return (
            d.getFullYear() === selectedYear && d.getMonth() === selectedMonth
          );
        });

        if (!hasCallsNow) {
          const latest = activePeriods[0];
          setSelectedYear(latest.year);
          setSelectedMonth(latest.month);
        }
      }
    }
  }, [selectedProjectId, filteredCallsByProject, activePeriods, selectedYear, selectedMonth]);

  // Filter calls for the selected month and year
  const callsForMonth = useMemo(() => {
    if (!filteredCallsByProject.length) return [];
    return filteredCallsByProject.filter((c) => {
      if (!c.Date) return false;
      const d = new Date(c.Date);
      if (isNaN(d.getTime())) return false;
      return (
        d.getFullYear() === selectedYear && d.getMonth() === selectedMonth
      );
    });
  }, [filteredCallsByProject, selectedMonth, selectedYear]);

  // Group calls by date of the month (1..31)
  const callsByDay = useMemo(() => {
    const map = {};
    callsForMonth.forEach((c) => {
      const d = new Date(c.Date);
      const day = d.getDate();
      if (!map[day]) map[day] = [];
      map[day].push(c);
    });
    return map;
  }, [callsForMonth]);

  // Extract all available dates across all months for the selected party and project
  // Strictly excludes any future months beyond current month & year (e.g. if current is Sep 2026, Oct 2026 is excluded)
  const allAvailableTaskDates = useMemo(() => {
    if (!filteredCallsByProject || !filteredCallsByProject.length) return [];
    const map = {};
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();

    filteredCallsByProject.forEach((c) => {
      if (!c.Date) return;
      const d = new Date(c.Date);
      if (isNaN(d.getTime())) return;

      const y = d.getFullYear();
      const m = d.getMonth();
      const day = d.getDate();

      // Exclude future months beyond current month & year (e.g. if current is Sep 2026, Oct 2026 must NOT appear)
      if (y > currentYear || (y === currentYear && m > currentMonth)) return;

      const dateKey = `${y}-${String(m + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;

      if (!map[dateKey]) {
        const dayName = d
          .toLocaleDateString("en-US", { weekday: "short" })
          .toUpperCase();
        const monthShort = MONTH_NAMES[m]?.slice(0, 3) || "";
        const dayFormatted = String(day).padStart(2, "0");
        const isWeekend = d.getDay() === 0 || d.getDay() === 6;

        map[dateKey] = {
          dateKey,
          year: y,
          monthIndex: m,
          monthName: MONTH_NAMES[m],
          monthShort,
          dayNumber: day,
          dayFormatted,
          dayName,
          displayDate: `${dayFormatted} ${monthShort}`,
          fullDateStr: `${dayFormatted} ${MONTH_NAMES[m]} ${y}`,
          dateObj: d,
          calls: [],
          isWeekend,
        };
      }

      map[dateKey].calls.push(c);
    });

    return Object.values(map)
      .sort((a, b) => {
        // Latest month first (descending by year and month index)
        if (b.year !== a.year) return b.year - a.year;
        if (b.monthIndex !== a.monthIndex) return b.monthIndex - a.monthIndex;
        // Within the month, chronological order (01, 02 ... 31)
        return a.dayNumber - b.dayNumber;
      })
      .map((item) => {
        const completedCount = item.calls.filter(
          (c) => (c.Status || "").toLowerCase() === "completed"
        ).length;
        const pendingCount = item.calls.filter(
          (c) => (c.Status || "").toLowerCase() === "pending"
        ).length;

        return {
          ...item,
          count: item.calls.length,
          completedCount,
          pendingCount,
        };
      });
  }, [filteredCallsByProject]);

  // Unique active months list for the quick filter chips - LATEST MONTH FIRST
  const uniqueTaskMonths = useMemo(() => {
    const map = {};
    allAvailableTaskDates.forEach((d) => {
      const key = `${d.year}-${d.monthIndex}`;
      if (!map[key]) {
        map[key] = {
          key,
          year: d.year,
          monthIndex: d.monthIndex,
          monthName: d.monthName,
          monthShort: d.monthShort,
          label: `${d.monthShort} ${d.year}`,
          count: 0,
          dateCount: 0,
        };
      }
      map[key].count += d.count;
      map[key].dateCount++;
    });

    // Sort descending so the latest month (e.g. Sep 2026) comes at the front (aage)
    return Object.values(map).sort((a, b) => {
      if (b.year !== a.year) return b.year - a.year;
      return b.monthIndex - a.monthIndex;
    });
  }, [allAvailableTaskDates]);

  // Task dates displayed in the scroller (all months or filtered by month)
  const taskDates = useMemo(() => {
    if (scrollerMonthFilter === "ALL") return allAvailableTaskDates;
    return allAvailableTaskDates.filter(
      (d) => `${d.year}-${d.monthIndex}` === scrollerMonthFilter
    );
  }, [allAvailableTaskDates, scrollerMonthFilter]);

  // When clicking a task date from any month
  const handleSelectTaskDate = (dateItem) => {
    if (!dateItem) return;
    setSelectedTaskDateKey(dateItem.dateKey);
    if (selectedMonth !== dateItem.monthIndex || selectedYear !== dateItem.year) {
      setSelectedMonth(dateItem.monthIndex);
      setSelectedYear(dateItem.year);
    }
  };

  // Guard: ensure selected month and year are never beyond current month & year
  useEffect(() => {
    const now = new Date();
    const curYear = now.getFullYear();
    const curMonth = now.getMonth();
    if (selectedYear > curYear) {
      setSelectedYear(curYear);
      setSelectedMonth(curMonth);
    } else if (selectedYear === curYear && selectedMonth > curMonth) {
      setSelectedMonth(curMonth);
    }
  }, [selectedYear, selectedMonth]);

  // Smart Auto-selection of date across all months
  useEffect(() => {
    if (allAvailableTaskDates.length > 0) {
      const today = new Date();
      const todayKey = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;

      // Check if selectedTaskDateKey is still valid in available dates
      const stillValid = allAvailableTaskDates.some(
        (d) => d.dateKey === selectedTaskDateKey
      );
      if (stillValid) return;

      // If today is in available dates, select today
      const todayDateObj = allAvailableTaskDates.find(
        (d) => d.dateKey === todayKey
      );
      if (todayDateObj) {
        handleSelectTaskDate(todayDateObj);
        return;
      }

      // Check if there is an active date matching current selectedYear & selectedMonth
      const matchingDatesCurrentMonth = allAvailableTaskDates.filter(
        (d) => d.year === selectedYear && d.monthIndex === selectedMonth
      );
      if (matchingDatesCurrentMonth.length > 0) {
        // Select the latest date in the current month
        handleSelectTaskDate(
          matchingDatesCurrentMonth[matchingDatesCurrentMonth.length - 1]
        );
        return;
      }

      // Otherwise auto-select the first available task date (which is in the latest month!)
      handleSelectTaskDate(allAvailableTaskDates[0]);
    } else {
      setSelectedTaskDateKey(null);
    }
  }, [allAvailableTaskDates]);

  // Auto-scroll the date scroller to bring active date into view
  useEffect(() => {
    if (selectedTaskDateKey && dateScrollerRef.current) {
      const activeEl = dateScrollerRef.current.querySelector(
        `[data-datekey="${selectedTaskDateKey}"]`
      );
      if (activeEl) {
        activeEl.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline: "center",
        });
      }
    }
  }, [selectedTaskDateKey]);

  // Scroll date scroller by direction
  const scrollDatesBy = (direction) => {
    if (dateScrollerRef.current) {
      const amount = direction * 180;
      dateScrollerRef.current.scrollBy({ left: amount, behavior: "smooth" });
    }
  };

  const currentTaskDateIndex = useMemo(() => {
    if (!selectedTaskDateKey || !taskDates.length) return -1;
    return taskDates.findIndex((d) => d.dateKey === selectedTaskDateKey);
  }, [taskDates, selectedTaskDateKey]);

  const handlePrevTaskDate = () => {
    if (currentTaskDateIndex > 0) {
      handleSelectTaskDate(taskDates[currentTaskDateIndex - 1]);
    }
  };

  const handleNextTaskDate = () => {
    if (
      currentTaskDateIndex >= 0 &&
      currentTaskDateIndex < taskDates.length - 1
    ) {
      handleSelectTaskDate(taskDates[currentTaskDateIndex + 1]);
    }
  };

  const selectedDateObj = useMemo(() => {
    if (!selectedTaskDateKey) return null;
    return (
      allAvailableTaskDates.find((d) => d.dateKey === selectedTaskDateKey) ||
      null
    );
  }, [allAvailableTaskDates, selectedTaskDateKey]);

  const selectedDateTasks = useMemo(() => {
    if (!selectedDateObj) return [];
    return selectedDateObj.calls || [];
  }, [selectedDateObj]);

  // Statistics for this month
  const monthStats = useMemo(() => {
    let completed = 0;
    let pending = 0;
    let other = 0;
    callsForMonth.forEach((c) => {
      const s = (c.Status || "").toLowerCase();
      if (s === "completed") completed++;
      else if (s === "pending") pending++;
      else other++;
    });
    return {
      total: callsForMonth.length,
      completed,
      pending,
      other,
      inProgress: other,
    };
  }, [callsForMonth]);

  // Month navigation (future months disabled)
  const isNextMonthDisabled = useMemo(() => {
    const now = new Date();
    const curYear = now.getFullYear();
    const curMonth = now.getMonth();
    return (
      selectedYear > curYear ||
      (selectedYear === curYear && selectedMonth >= curMonth)
    );
  }, [selectedYear, selectedMonth]);

  const handlePrevMonth = () => {
    if (selectedMonth === 0) {
      setSelectedMonth(11);
      setSelectedYear((prev) => prev - 1);
    } else {
      setSelectedMonth((prev) => prev - 1);
    }
  };

  const handleNextMonth = () => {
    if (isNextMonthDisabled) return;
    if (selectedMonth === 11) {
      setSelectedMonth(0);
      setSelectedYear((prev) => prev + 1);
    } else {
      setSelectedMonth((prev) => prev + 1);
    }
  };

  const handleToday = () => {
    const today = new Date();
    setSelectedMonth(today.getMonth());
    setSelectedYear(today.getFullYear());
  };

  // Days calculations for calendar grid
  const calendarDays = useMemo(() => {
    const firstDayOfWeek = new Date(selectedYear, selectedMonth, 1).getDay(); // 0 is Sunday
    const daysInMonth = new Date(selectedYear, selectedMonth + 1, 0).getDate();
    const daysInPrevMonth = new Date(selectedYear, selectedMonth, 0).getDate();

    const days = [];

    // Leading padding days from previous month
    for (let i = firstDayOfWeek - 1; i >= 0; i--) {
      days.push({
        dayNumber: daysInPrevMonth - i,
        isCurrentMonth: false,
        date: new Date(selectedYear, selectedMonth - 1, daysInPrevMonth - i),
      });
    }

    // Current month days
    for (let i = 1; i <= daysInMonth; i++) {
      days.push({
        dayNumber: i,
        isCurrentMonth: true,
        date: new Date(selectedYear, selectedMonth, i),
        calls: callsByDay[i] || [],
      });
    }

    // Trailing padding days to fill 7 columns
    const totalSlots = Math.ceil(days.length / 7) * 7;
    const remainingSlots = totalSlots - days.length;
    for (let i = 1; i <= remainingSlots; i++) {
      days.push({
        dayNumber: i,
        isCurrentMonth: false,
        date: new Date(selectedYear, selectedMonth + 1, i),
      });
    }

    return days;
  }, [selectedMonth, selectedYear, callsByDay]);

  // Group days into 7-day weeks
  const calendarWeeks = useMemo(() => {
    const weeks = [];
    for (let i = 0; i < calendarDays.length; i += 7) {
      const weekDays = calendarDays.slice(i, i + 7);
      const hasCalls = weekDays.some(
        (d) => d.calls && d.calls.length > 0
      );
      const totalCalls = weekDays.reduce(
        (sum, d) => sum + (d.calls ? d.calls.length : 0),
        0
      );
      weeks.push({
        weekIndex: Math.floor(i / 7),
        days: weekDays,
        hasCalls,
        totalCalls,
      });
    }
    return weeks;
  }, [calendarDays]);

  const isToday = (dayObj) => {
    if (!dayObj.isCurrentMonth) return false;
    const today = new Date();
    return (
      today.getDate() === dayObj.dayNumber &&
      today.getMonth() === selectedMonth &&
      today.getFullYear() === selectedYear
    );
  };

  const partyId = selectedParty
    ? selectedParty._id || selectedParty.CompanyId
    : null;

  return (
    <div className="w-full max-w-full overflow-hidden space-y-2">
      {/* Sleek Scrollbars for Cells and Horizontal Calendar Table */}
      <style>{`
        .calendar-cell-scroll::-webkit-scrollbar {
          width: 4px !important;
          height: 4px !important;
        }
        .calendar-cell-scroll::-webkit-scrollbar-track {
          background: transparent !important;
        }
        .calendar-cell-scroll::-webkit-scrollbar-thumb {
          background-color: #94a3b8 !important;
          border-radius: 9999px !important;
        }
        .calendar-cell-scroll::-webkit-scrollbar-thumb:hover {
          background-color: #64748b !important;
        }
        .calendar-cell-scroll {
          scrollbar-width: thin !important;
          scrollbar-color: #94a3b8 transparent !important;
        }

        /* Prominent Horizontal Scrollbar for Calendar Table */
        .calendar-table-scroll::-webkit-scrollbar {
          height: 9px !important;
        }
        .calendar-table-scroll::-webkit-scrollbar-track {
          background: #f1f5f9 !important;
          border-radius: 6px !important;
        }
        .calendar-table-scroll::-webkit-scrollbar-thumb {
          background-color: #154D75 !important;
          border-radius: 6px !important;
        }
        .calendar-table-scroll::-webkit-scrollbar-thumb:hover {
          background-color: #0c334f !important;
        }
        .calendar-table-scroll {
          scrollbar-width: thin !important;
          scrollbar-color: #154D75 #f1f5f9 !important;
        }
      `}</style>

      {/* =========================================================================
          1. IN-PAGE CALENDAR VIEW SETUP & FILTER CARD (Normal, Clean Dashboard Typography)
         ========================================================================= */}
      <div className="w-full max-w-full rounded-xl border border-slate-200 bg-white shadow-2xs relative z-30">
        {/* Setup Header Bar */}
        <div
          onClick={() => {
            if (selectedParty) {
              setIsFilterPanelOpen((prev) => !prev);
            }
          }}
          className={`px-3 py-2 sm:px-4 sm:py-2.5 flex flex-col sm:flex-row sm:items-center justify-between gap-1.5 sm:gap-2 transition-all select-none ${selectedParty
            ? "cursor-pointer hover:bg-slate-50 bg-slate-50/70"
            : "bg-slate-50"
            } ${!selectedParty || isFilterPanelOpen
              ? "rounded-t-xl border-b border-slate-200"
              : "rounded-xl"
            }`}
          title={
            selectedParty
              ? isFilterPanelOpen
                ? "Click to collapse filter panel"
                : "Click to change party, project, or month"
              : ""
          }
        >
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <div className="w-6 h-6 rounded-md bg-[#154D75] text-white flex items-center justify-center shadow-2xs shrink-0">
              <FaCalendarAlt className="text-xs" />
            </div>
            <div className="min-w-0">
              <span className="text-xs font-semibold text-slate-800 mr-2">
                Calendar View Setup
              </span>
              {selectedParty && (
                <span className="text-xs text-slate-600 truncate font-normal">
                  &bull; Party:{" "}
                  <span className="font-semibold text-[#154D75]">
                    {selectedParty.CompanyName}
                  </span>
                  {selectedProjectId !== "ALL" && (
                    <>
                      {" "}&bull; Project:{" "}
                      <span className="font-medium text-slate-700">
                        {partyProjects.find((p) => p.id === selectedProjectId)
                          ?.name ||
                          (selectedProjectId === "NO_PROJECT"
                            ? "General"
                            : selectedProjectId)}
                      </span>
                    </>
                  )}
                  {" "}&bull; Period:{" "}
                  <span className="font-medium text-slate-800">
                    {MONTH_NAMES[selectedMonth]} {selectedYear}
                  </span>
                </span>
              )}
            </div>
          </div>

          <div className="flex items-center gap-1.5 shrink-0 self-end sm:self-auto">
            {selectedParty && (
              <span className="text-xs font-medium px-2 py-0.5 rounded-md border border-slate-300 text-slate-600 bg-white">
                {isFilterPanelOpen ? "▲ Collapse" : "▼ Change Filter"}
              </span>
            )}
            {selectedParty && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsFilterPanelOpen((prev) => !prev);
                }}
                className="p-1 rounded-md text-slate-600 hover:text-black hover:bg-slate-200 transition-colors cursor-pointer"
                title={isFilterPanelOpen ? "Collapse panel" : "Expand panel"}
              >
                {isFilterPanelOpen ? (
                  <FaChevronUp className="text-xs" />
                ) : (
                  <FaChevronDown className="text-xs" />
                )}
              </button>
            )}
          </div>
        </div>

        {/* Setup Body - Clean Normal Font Weights & Labels */}
        {(!selectedParty || isFilterPanelOpen) && (
          <div className="p-3 sm:p-4 text-xs bg-white rounded-b-xl">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-3 items-end">
              {/* 1. SELECT PARTY */}
              <div
                className="col-span-1 sm:col-span-2 lg:col-span-4 relative"
                ref={partyDropdownRef}
              >
                <label className="block text-xs font-medium text-slate-700 mb-1 flex items-center justify-between">
                  <span className="inline-flex items-center gap-1 text-[#154D75] font-semibold">
                    <FaBuilding className="text-xs" /> Select Party / Company:
                  </span>
                  {selectedParty && (
                    <span className="font-medium px-2 py-0.5 rounded border border-emerald-200 text-xs text-emerald-800 bg-emerald-50">
                      Total: {selectedParty.total || 0} Calls
                    </span>
                  )}
                </label>

                {/* Dropdown Trigger Button */}
                <button
                  type="button"
                  onClick={() => setIsPartyDropdownOpen((prev) => !prev)}
                  className="w-full px-3 py-2 rounded-lg text-xs font-normal border border-slate-300 hover:border-[#154D75] focus:border-[#154D75] bg-white text-slate-800 shadow-xs flex items-center justify-between transition-colors cursor-pointer text-left"
                >
                  <div className="truncate pr-2">
                    {selectedParty ? (
                      <span>
                        <span className="font-semibold text-slate-900">
                          {selectedParty.CompanyName}
                        </span>{" "}
                        <span className="text-slate-500 font-normal">
                          [Total: {selectedParty.total || 0} | Done:{" "}
                          {selectedParty.completed || 0} | Pending:{" "}
                          {selectedParty.pending || 0}]
                        </span>
                      </span>
                    ) : (
                      <span className="text-slate-400 font-normal">
                        -- Choose Party / Client ({filteredParties.length} available) --
                      </span>
                    )}
                  </div>
                  <FaChevronDown
                    className={`text-xs transition-transform duration-200 shrink-0 text-slate-500 ${isPartyDropdownOpen ? "rotate-180 text-[#154D75]" : ""
                      }`}
                  />
                </button>

                {/* Dropdown Floating Panel */}
                {isPartyDropdownOpen && (
                  <div className="absolute left-0 right-0 top-full mt-1 rounded-xl shadow-xl z-50 overflow-hidden animate-in fade-in slide-in-from-top-1 duration-150 border border-slate-300 bg-white text-slate-800">
                    <div className="p-2 border-b border-slate-200 bg-slate-50 sticky top-0 z-10">
                      <div className="relative">
                        <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs" />
                        <input
                          ref={searchInputRef}
                          type="text"
                          placeholder="Search party by name..."
                          value={partySearchTerm}
                          onChange={(e) => setPartySearchTerm(e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                          className="w-full pl-8 pr-7 py-1.5 rounded-lg text-xs font-normal border border-slate-300 focus:border-[#154D75] bg-white text-slate-800 placeholder:text-slate-400 focus:outline-none"
                        />
                        {partySearchTerm && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setPartySearchTerm("");
                            }}
                            className="absolute right-2.5 top-1/2 -translate-y-1/2 p-0.5 cursor-pointer text-slate-400 hover:text-slate-700"
                          >
                            <FaTimes className="text-[10px]" />
                          </button>
                        )}
                      </div>
                    </div>

                    <div className="max-h-56 overflow-y-auto divide-y divide-slate-100 calendar-cell-scroll">
                      {filteredParties.length === 0 ? (
                        <div className="p-3 text-center text-xs font-normal text-slate-500">
                          No parties found matching "{partySearchTerm}"
                        </div>
                      ) : (
                        filteredParties.map((p) => {
                          const isSelected =
                            selectedParty &&
                            (selectedParty._id === p._id ||
                              selectedParty.CompanyId === p.CompanyId);
                          return (
                            <div
                              key={p._id || p.CompanyId}
                              onClick={() => handleSelectParty(p)}
                              className={`p-2 sm:p-2.5 flex items-center justify-between cursor-pointer transition-colors ${isSelected
                                ? "bg-blue-50 text-[#154D75] border-l-4 border-l-[#154D75]"
                                : "hover:bg-slate-50 text-slate-800"
                                }`}
                            >
                              <div className="flex-1 min-w-0 pr-2">
                                <div className="flex items-center gap-1.5">
                                  <span className="font-medium text-xs truncate text-slate-900">
                                    {p.CompanyName}
                                  </span>
                                  {p.Type && (
                                    <span className="text-[10px] font-medium uppercase px-1.5 py-0.2 rounded border border-blue-200 text-[#154D75] bg-blue-50">
                                      {p.Type}
                                    </span>
                                  )}
                                </div>
                                <div className="text-[11px] text-slate-500 font-normal mt-0.5">
                                  {p.Area ? `${p.Area} • ` : ""}Total Calls:{" "}
                                  <strong className="text-slate-700 font-medium">
                                    {p.total || 0}
                                  </strong>
                                </div>
                              </div>
                              <div className="flex items-center gap-1 text-[11px] font-medium shrink-0">
                                <span className="text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded">
                                  ✓ {p.completed || 0}
                                </span>
                                <span className="text-rose-700 bg-rose-50 border border-rose-200 px-1.5 py-0.5 rounded">
                                  ⏱ {p.pending || 0}
                                </span>
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* 2. SELECT PROJECT */}
              <div className="col-span-1 sm:col-span-1 lg:col-span-3">
                <label className="block text-xs font-medium text-slate-700 mb-1 flex items-center justify-between">
                  <span className="inline-flex items-center gap-1 text-[#154D75] font-semibold">
                    <FaProjectDiagram className="text-xs" /> Project Filter:
                  </span>
                  {partyProjects.length > 0 && (
                    <span className="font-normal text-xs text-slate-500">
                      {partyProjects.length} found
                    </span>
                  )}
                </label>
                <select
                  value={selectedProjectId}
                  onChange={(e) => setSelectedProjectId(e.target.value)}
                  disabled={!selectedParty || partyCallDetailLoading}
                  className="w-full px-3 py-2 rounded-lg text-xs font-normal border border-slate-300 focus:border-[#154D75] bg-white text-slate-800 shadow-xs focus:outline-none disabled:bg-slate-100 disabled:text-slate-400 disabled:cursor-not-allowed"
                >
                  <option value="ALL">
                    All Projects ({allCalls.length} calls)
                  </option>
                  {partyProjects.map((proj) => (
                    <option key={proj.id} value={proj.id}>
                      {proj.name} ({proj.count} calls)
                    </option>
                  ))}
                  {noProjectCount > 0 && (
                    <option value="NO_PROJECT">
                      General / No Project ({noProjectCount} calls)
                    </option>
                  )}
                </select>
              </div>

              {/* 3. SELECT MONTH */}
              <div className="col-span-1 sm:col-span-1 lg:col-span-3">
                <label className="block text-xs font-medium text-slate-700 mb-1">
                  <span className="inline-flex items-center gap-1 text-[#154D75] font-semibold">
                    <FaCalendarAlt className="text-xs" /> Month:
                  </span>
                </label>
                <select
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(Number(e.target.value))}
                  disabled={!selectedParty || partyCallDetailLoading}
                  className="w-full px-3 py-2 rounded-lg text-xs font-normal border border-slate-300 focus:border-[#154D75] bg-white text-slate-800 shadow-xs focus:outline-none disabled:bg-slate-100 disabled:text-slate-400 disabled:cursor-not-allowed"
                >
                  {MONTH_NAMES.map((name, idx) => {
                    const now = new Date();
                    const curYear = now.getFullYear();
                    const curMonth = now.getMonth();
                    const isFuture =
                      selectedYear > curYear ||
                      (selectedYear === curYear && idx > curMonth);
                    if (isFuture) return null;
                    const c = monthCountsForYear[idx] || { total: 0 };
                    return (
                      <option key={idx} value={idx}>
                        {name} ({c.total} calls)
                      </option>
                    );
                  })}
                </select>
              </div>

              {/* 4. SELECT YEAR */}
              <div className="col-span-1 sm:col-span-1 lg:col-span-2">
                <label className="block text-xs font-medium text-slate-700 mb-1">
                  <span className="font-semibold text-[#154D75]">Year:</span>
                </label>
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(Number(e.target.value))}
                  disabled={!selectedParty || partyCallDetailLoading}
                  className="w-full px-3 py-2 rounded-lg text-xs font-normal border border-slate-300 focus:border-[#154D75] bg-white text-slate-800 shadow-xs focus:outline-none disabled:bg-slate-100 disabled:text-slate-400 disabled:cursor-not-allowed"
                >
                  {availableYears.map((y) => (
                    <option key={y.year} value={y.year}>
                      {y.year} {y.count > 0 ? `(${y.count})` : ""}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Quick Active Months Navigator */}
            {selectedParty && activePeriods.length > 0 && (
              <div className="mt-2.5 pt-2.5 border-t border-slate-200 flex items-center gap-1.5 overflow-x-auto pb-1 calendar-cell-scroll">
                <span className="text-xs font-medium text-slate-600 shrink-0">
                  Active Months:
                </span>
                <div className="flex items-center gap-1 flex-nowrap">
                  {activePeriods.map((p) => {
                    const isActive =
                      p.year === selectedYear && p.month === selectedMonth;
                    return (
                      <button
                        key={p.key}
                        type="button"
                        onClick={() => {
                          setSelectedYear(p.year);
                          setSelectedMonth(p.month);
                        }}
                        className={`px-2.5 py-1 rounded-md text-xs transition-all cursor-pointer shrink-0 border ${isActive
                          ? "bg-[#154D75] text-white border-[#154D75] font-medium shadow-xs"
                          : "bg-white text-slate-700 border-slate-300 hover:border-[#154D75] hover:bg-slate-50 font-normal"
                          }`}
                      >
                        {p.monthName.slice(0, 3)} {p.year}{" "}
                        <span
                          className={`ml-1 text-[11px] px-1.5 py-0.2 rounded-full ${isActive
                            ? "bg-white/25 text-white font-medium"
                            : "bg-slate-100 text-slate-600 font-normal"
                            }`}
                        >
                          {p.count}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Loading Indicator */}
      {partyCallDetailLoading && (
        <div className="p-6 bg-white rounded-xl shadow-xs border border-slate-200 flex items-center justify-center">
          <Loader />
        </div>
      )}

      {/* =========================================================================
          2. PARTY SELECTION CARDS (COMPACT / CHOTA DESIGN)
         ========================================================================= */}
      {!selectedParty ? (
        <div className="bg-white rounded-xl p-3 sm:p-3.5 shadow-xs border border-slate-200/90">
          {/* Section Header with Title, Search, Sort & Count */}
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-2.5 mb-2.5 pb-2 border-b border-slate-200">
            <div>
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-md bg-blue-50 text-[#154D75] border border-blue-200/80 flex items-center justify-center shrink-0 shadow-2xs">
                  <FaBuilding className="text-[11px]" />
                </div>
                <h3 className="text-xs sm:text-sm font-bold text-slate-900 tracking-tight">
                  Select a Party to View Calendar
                </h3>
              </div>
              <p className="text-[11px] text-slate-500 mt-0.5 ml-8">
                Choose any client below to inspect their monthwise call activity & calendar
              </p>
            </div>

            {/* Quick Controls: Search, Sort & Badge */}
            <div className="flex items-center gap-1.5 flex-wrap sm:flex-nowrap">
              {/* Quick Search */}
              <div className="relative min-w-[180px] flex-1 sm:flex-initial">
                <FaSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 text-[10px]" />
                <input
                  type="text"
                  placeholder="Search party..."
                  value={partySearchTerm}
                  onChange={(e) => setPartySearchTerm(e.target.value)}
                  className="w-full pl-7 pr-6 py-1 rounded-md text-xs border border-slate-300 focus:border-[#154D75] bg-white text-slate-800 placeholder:text-slate-400 focus:outline-none shadow-2xs"
                />
                {partySearchTerm && (
                  <button
                    type="button"
                    onClick={() => setPartySearchTerm("")}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 text-[9px] cursor-pointer"
                  >
                    <FaTimes />
                  </button>
                )}
              </div>

              {/* Sort Selector */}
              <select
                value={gridSortBy}
                onChange={(e) => setGridSortBy(e.target.value)}
                className="px-2 py-1 rounded-md text-xs font-semibold border border-slate-300 bg-white text-slate-700 shadow-2xs focus:outline-none focus:border-[#154D75] cursor-pointer"
              >
                <option value="name">Sort: Name (A-Z)</option>
                <option value="total">Sort: Most Calls</option>
                <option value="pending">Sort: Most Pending</option>
              </select>

              {/* Parties Counter Badge */}
              <div className="text-[11px] font-bold px-2 py-0.5 rounded-full border border-blue-200 bg-blue-50 text-[#154D75] shrink-0 shadow-2xs">
                {displayedParties.length} / {parties.length} Parties
              </div>
            </div>
          </div>

          {/* Category Filter Pills Bar */}
          <div className="flex items-center gap-1 flex-wrap mb-2.5">
            <span className="text-[11px] font-bold text-slate-500 mr-1 flex items-center gap-1">
              <FaFilter className="text-[9px] text-slate-400" /> Filter:
            </span>
            {availableCategories.map((cat) => {
              const isActive = gridCategory === cat;
              const count = partyTypeCounts[cat] || 0;
              const label =
                cat === "ALL"
                  ? "All"
                  : cat === "PENDING_ONLY"
                    ? "Pending / Priority"
                    : cat;

              return (
                <button
                  key={cat}
                  type="button"
                  onClick={() => setGridCategory(cat)}
                  className={`px-2 py-0.5 rounded-md text-[11px] font-bold transition-all cursor-pointer border flex items-center gap-1 ${isActive
                    ? "bg-[#154D75] text-white border-[#154D75] shadow-xs"
                    : "bg-white text-slate-700 border-slate-200 hover:border-slate-300 hover:bg-slate-50"
                    }`}
                >
                  <span>{label}</span>
                  <span
                    className={`text-[9px] px-1 py-0.2 rounded-full font-bold ${isActive
                      ? "bg-white/20 text-white"
                      : "bg-slate-100 text-slate-600"
                      }`}
                  >
                    {count}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Cards Grid - Compact */}
          {displayedParties.length === 0 ? (
            <div className="p-6 text-center bg-slate-50/60 rounded-xl border border-dashed border-slate-200 my-2">
              <FaBuilding className="text-xl text-slate-300 mx-auto mb-1.5" />
              <p className="text-xs font-semibold text-slate-600">
                No parties found matching current filter or search criteria.
              </p>
              {(partySearchTerm || gridCategory !== "ALL") && (
                <button
                  type="button"
                  onClick={() => {
                    setPartySearchTerm("");
                    setGridCategory("ALL");
                  }}
                  className="mt-2.5 px-3 py-1 bg-[#154D75] text-white text-xs font-bold rounded-md shadow-xs hover:bg-[#0d3452] transition-colors cursor-pointer"
                >
                  Clear Filters
                </button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-2.5 max-h-[66vh] overflow-y-auto pr-1 calendar-cell-scroll">
              {displayedParties.map((p) => {
                const total = p.total || 0;
                const completed = p.completed || 0;
                const pending = p.pending || 0;
                const isPriority = pending > 0 || p.Priority?.toLowerCase() === "on";
                const initials = getPartyInitials(p.CompanyName);
                const typeStyle = getTypeBadgeStyle(p.Type);

                return (
                  <div
                    key={p._id || p.CompanyId}
                    onClick={() => handleSelectParty(p)}
                    className="group relative bg-white rounded-lg border border-slate-200 hover:border-[#154D75] shadow-2xs hover:shadow-md hover:-translate-y-0.5 transition-all duration-150 cursor-pointer flex flex-col justify-between overflow-hidden text-slate-800"
                    title={`Click to view calendar for ${p.CompanyName}`}
                  >
                    {/* Top indicator color strip */}
                    <div
                      className={`h-0.5 w-full transition-all duration-150 ${isPriority
                        ? "bg-gradient-to-r from-rose-500 to-amber-500"
                        : "bg-slate-100 group-hover:bg-[#154D75]"
                        }`}
                    />

                    <div className="p-2.5 flex flex-col flex-1 justify-between">
                      <div>
                        {/* Header: Avatar, Type Badge & Priority Badge */}
                        <div className="flex items-center justify-between gap-1 mb-1">
                          <div className="flex items-center gap-1.5 min-w-0">
                            <div
                              className={`w-5 h-5 rounded flex items-center justify-center font-black text-[9px] shadow-2xs shrink-0 transition-all duration-150 ${isPriority
                                ? "bg-rose-50 text-rose-700 border border-rose-200 group-hover:bg-rose-600 group-hover:text-white"
                                : "bg-slate-100 text-[#154D75] border border-slate-200 group-hover:bg-[#154D75] group-hover:text-white"
                                }`}
                              title={p.CompanyName}
                            >
                              {initials}
                            </div>
                            <span
                              className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.2 rounded border shadow-2xs truncate max-w-[120px] ${typeStyle}`}
                              title={p.Type || "CLIENT"}
                            >
                              {p.Type || "CLIENT"}
                            </span>
                          </div>

                          {isPriority && (
                            <span className="inline-flex items-center gap-1 text-[9px] font-bold text-rose-700 bg-rose-50 border border-rose-200 px-1.5 py-0.2 rounded-full shadow-2xs shrink-0">
                              <span className="w-1 h-1 rounded-full bg-rose-600 animate-pulse" />
                              Priority
                            </span>
                          )}
                        </div>

                        {/* Party / Company Name - Compact single line truncate */}
                        <h4
                          className="text-xs font-bold text-slate-900 group-hover:text-[#154D75] transition-colors truncate mb-1.5 leading-tight"
                          title={p.CompanyName}
                        >
                          {p.CompanyName}
                        </h4>
                      </div>

                      {/* Statistics & Progress - Compact */}
                      <div>
                        {/* 3 Metric Pills */}
                        <div className="grid grid-cols-3 gap-1">
                          {/* Total */}
                          <div className="bg-slate-50 group-hover:bg-blue-50/40 rounded py-0.5 px-0.5 text-center border border-slate-200/70 transition-colors">
                            <span className="block text-[8px] font-bold uppercase tracking-wider text-slate-400 leading-tight">
                              Total
                            </span>
                            <span className="text-[11px] sm:text-xs font-black text-slate-800 leading-tight block">
                              {total}
                            </span>
                          </div>

                          {/* Solved */}
                          <div className="bg-emerald-50/70 group-hover:bg-emerald-50 rounded py-0.5 px-0.5 text-center border border-emerald-200/70 transition-colors">
                            <span className="block text-[8px] font-bold uppercase tracking-wider text-emerald-600 leading-tight flex items-center justify-center gap-0.5">
                              <FaCheck className="text-[7px]" /> Solved
                            </span>
                            <span className="text-[11px] sm:text-xs font-black text-emerald-700 leading-tight block">
                              {completed}
                            </span>
                          </div>

                          {/* Pending */}
                          <div
                            className={`rounded py-0.5 px-0.5 text-center border leading-tight transition-colors ${pending > 0
                              ? "bg-rose-50/90 border-rose-200/90 text-rose-700 shadow-2xs"
                              : "bg-slate-50/50 border-slate-200/50 text-slate-400"
                              }`}
                          >
                            <span
                              className={`block text-[8px] font-bold uppercase tracking-wider leading-tight flex items-center justify-center gap-0.5 ${pending > 0 ? "text-rose-700" : "text-slate-400"
                                }`}
                            >
                              <FaClock className="text-[7px]" /> Pending
                            </span>
                            <span
                              className={`text-[11px] sm:text-xs font-black leading-tight block ${pending > 0
                                ? "text-rose-700"
                                : "text-slate-500 font-bold"
                                }`}
                            >
                              {pending}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ) : (
        /* =========================================================================
            3. CALENDAR CONTENT
           ========================================================================= */
        <>
          {/* Header Banner */}
          <div className="w-full max-w-full rounded-xl shadow-xs border border-slate-200 bg-white overflow-hidden">
            <div className="px-3 py-2 sm:px-4 sm:py-2.5 flex flex-col md:flex-row md:items-center justify-between gap-2 border-b border-slate-200 bg-white text-slate-800">
              <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-lg bg-[#154D75] text-white flex items-center justify-center shadow-xs shrink-0">
                  <FaBuilding className="text-sm sm:text-base" />
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h2 className="text-xs sm:text-base font-bold tracking-tight text-slate-900 truncate">
                      {selectedParty.CompanyName}
                    </h2>
                    {selectedParty?.Type && (
                      <span className="text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded border border-blue-200 bg-blue-50 text-[#154D75]">
                        {selectedParty.Type}
                      </span>
                    )}
                  </div>
                  <p className="text-xs font-normal text-slate-600 mt-0.5 break-words">
                    Monthwise ERP Support &bull; Period:{" "}
                    <span className="font-semibold text-slate-800">
                      {MONTH_NAMES[selectedMonth]} {selectedYear}
                    </span>
                    {selectedProjectId !== "ALL" && (
                      <>
                        {" "}&bull; Project:{" "}
                        <span className="font-medium px-1.5 py-0.5 rounded border border-blue-200 text-[#154D75] bg-blue-50">
                          {partyProjects.find((p) => p.id === selectedProjectId)
                            ?.name ||
                            (selectedProjectId === "NO_PROJECT"
                              ? "General"
                              : selectedProjectId)}
                        </span>
                      </>
                    )}
                  </p>
                </div>
              </div>

              {/* Action Controls */}
              <div className="flex items-center justify-between sm:justify-end gap-1.5 flex-wrap pt-1 sm:pt-0 border-t sm:border-t-0 border-slate-200">
                {/* View Mode Toggle: Date Tasks View vs Month Grid View */}
                <div className="inline-flex rounded-lg border border-slate-300 bg-slate-100 p-0.5 shadow-2xs">
                  <button
                    type="button"
                    onClick={() => setViewMode("date-nav")}
                    className={`inline-flex items-center gap-1.5 px-2.5 sm:px-3 py-0.5 sm:py-1 rounded-md text-xs font-bold transition-all cursor-pointer ${viewMode === "date-nav"
                      ? "bg-[#154D75] text-white shadow-xs"
                      : "text-slate-700 hover:text-black hover:bg-white/60"
                      }`}
                    title="Date-focused Task Navigation View"
                  >
                    <FaCalendarDay className="text-[11px]" />
                    <span>Date View</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setViewMode("month-grid")}
                    className={`inline-flex items-center gap-1.5 px-2.5 sm:px-3 py-0.5 sm:py-1 rounded-md text-xs font-bold transition-all cursor-pointer ${viewMode === "month-grid"
                      ? "bg-[#154D75] text-white shadow-xs"
                      : "text-slate-700 hover:text-black hover:bg-white/60"
                      }`}
                    title="Full 7-Column Month Calendar Grid View"
                  >
                    <FaTh className="text-[11px]" />
                    <span>Grid View</span>
                  </button>
                </div>

                {/* Expand / Collapse All Controls (Grid View Only) */}
                {viewMode === "month-grid" && (
                  <div className="inline-flex rounded-lg border border-slate-300 bg-white p-0.5 shadow-2xs">
                    <button
                      type="button"
                      onClick={() => setIsExpandedAll(true)}
                      className={`inline-flex items-center gap-1 px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-md text-xs font-medium transition-colors cursor-pointer ${isExpandedAll
                        ? "bg-[#154D75] text-white shadow-xs"
                        : "text-slate-700 hover:text-black hover:bg-slate-100"
                        }`}
                      title="Expand all calls"
                    >
                      <FaExpandAlt className="text-[10px]" />
                      <span>Expand</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setIsExpandedAll(false)}
                      className={`inline-flex items-center gap-1 px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-md text-xs font-medium transition-colors cursor-pointer ${!isExpandedAll
                        ? "bg-[#154D75] text-white shadow-xs"
                        : "text-slate-700 hover:text-black hover:bg-slate-100"
                        }`}
                      title="Collapse all calls"
                    >
                      <FaCompressAlt className="text-[10px]" />
                      <span>Collapse</span>
                    </button>
                  </div>
                )}

                {/* Print / Export Report Controls */}
                <div className="inline-flex rounded-lg border border-slate-300 bg-white p-0.5 shadow-2xs">
                  <button
                    type="button"
                    onClick={handleOpenPrintChoice}
                    className="inline-flex items-center gap-1.5 px-2.5 sm:px-3 py-0.5 sm:py-1 rounded-md text-xs font-semibold text-white bg-[#154D75] hover:bg-[#0f3856] shadow-xs transition-colors cursor-pointer"
                    title="Print or Export Calendar Report / Annual Summary"
                  >
                    <FaPrint className="text-[10px] sm:text-[11px]" />
                    <span>Print / Export</span>
                  </button>
                </div>
              </div>
            </div>

            {/* Month Navigation & Status Legend - Royal Blue */}
            <div className="text-white px-3 py-2 flex flex-col sm:flex-row items-center justify-between gap-2 shadow-inner border-y bg-[#0070c0] border-blue-400">
              {/* Month Navigation */}
              <div className="flex items-center justify-between w-full sm:w-auto gap-2">
                <h3 className="text-xs sm:text-sm md:text-base font-bold tracking-wide uppercase text-white truncate">
                  {MONTH_NAMES[selectedMonth]} {selectedYear}
                </h3>

                <div className="flex items-center gap-1 shrink-0">
                  <button
                    type="button"
                    onClick={handlePrevMonth}
                    className="p-1 sm:p-1.5 rounded bg-white/20 hover:bg-white/30 text-white transition-colors cursor-pointer"
                    title="Previous Month"
                  >
                    <FaChevronLeft className="text-[10px] sm:text-xs" />
                  </button>
                  <button
                    type="button"
                    onClick={handleToday}
                    className="px-2 py-0.5 sm:py-1 rounded bg-white/20 hover:bg-white/30 text-white text-[11px] font-semibold transition-colors cursor-pointer"
                    title="Go to Today"
                  >
                    Today
                  </button>
                  <button
                    type="button"
                    onClick={handleNextMonth}
                    disabled={isNextMonthDisabled}
                    className={`p-1 sm:p-1.5 rounded text-white transition-colors ${isNextMonthDisabled
                      ? "bg-white/10 opacity-30 cursor-not-allowed"
                      : "bg-white/20 hover:bg-white/30 cursor-pointer"
                      }`}
                    title={
                      isNextMonthDisabled
                        ? "Future months are not available"
                        : "Next Month"
                    }
                  >
                    <FaChevronRight className="text-[10px] sm:text-xs" />
                  </button>
                </div>
              </div>

              {/* Legend Badges */}
              <div className="flex items-center justify-center sm:justify-end gap-1.5 sm:gap-2.5 text-xs font-medium flex-wrap w-full sm:w-auto">
                <div className="flex items-center gap-1 bg-white/15 px-2 py-0.5 rounded-full border border-white/25">
                  <FaCheckCircle className="text-emerald-400 text-xs" />
                  <span className="text-white">
                    <span className="sm:hidden">Done</span>
                    <span className="hidden sm:inline">= Completed</span>
                  </span>
                  <span className="bg-emerald-500/90 text-white text-[10px] px-1.5 rounded-full font-semibold">
                    {monthStats.completed}
                  </span>
                </div>

                <div className="flex items-center gap-1 bg-white/15 px-2 py-0.5 rounded-full border border-white/25">
                  <FaClock className="text-amber-300 text-xs" />
                  <span className="text-white">
                    <span className="sm:hidden">Review</span>
                    <span className="hidden sm:inline">= Under Review</span>
                  </span>
                  <span className="bg-amber-500/90 text-white text-[10px] px-1.5 rounded-full font-semibold">
                    {monthStats.other}
                  </span>
                </div>

                <div className="flex items-center gap-1 bg-white/15 px-2 py-0.5 rounded-full border border-white/25">
                  <FaTimesCircle className="text-rose-400 text-xs" />
                  <span className="text-white">
                    <span className="sm:hidden">Pending</span>
                    <span className="hidden sm:inline">= Pending</span>
                  </span>
                  <span className="bg-rose-500/90 text-white text-[10px] px-1.5 rounded-full font-semibold">
                    {monthStats.pending}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Notice if 0 calls in selected month */}
          {callsForMonth.length === 0 && (
            <div className="p-3 sm:p-4 rounded-xl flex flex-col sm:flex-row sm:items-center justify-between gap-2 text-xs border border-amber-300 bg-amber-50 text-amber-900 font-normal">
              <div className="flex items-center gap-2">
                <FaInfoCircle className="text-sm sm:text-base shrink-0 text-amber-700" />
                <span>
                  No call records found for{" "}
                  <strong className="font-semibold">
                    {selectedParty.CompanyName}
                  </strong>
                  {selectedProjectId !== "ALL" && (
                    <>
                      {" (Project: "}
                      <strong className="font-semibold">
                        {partyProjects.find((p) => p.id === selectedProjectId)
                          ?.name ||
                          (selectedProjectId === "NO_PROJECT"
                            ? "General"
                            : selectedProjectId)}
                      </strong>
                      {")"}
                    </>
                  )}{" "}
                  in{" "}
                  <strong className="font-semibold">
                    {MONTH_NAMES[selectedMonth]} {selectedYear}
                  </strong>
                  .
                </span>
              </div>
              {selectedProjectId !== "ALL" && (
                <button
                  type="button"
                  onClick={() => setSelectedProjectId("ALL")}
                  className="px-2.5 py-1 rounded-lg font-medium text-xs transition-colors cursor-pointer shrink-0 border border-amber-400 bg-white text-amber-900 hover:bg-amber-100"
                >
                  View All Projects
                </button>
              )}
            </div>
          )}

          {/* =========================================================================
              3. CALENDAR CONTENT: DATE NAVIGATION VIEW OR 7-COLUMN GRID
             ========================================================================= */}
          {viewMode === "date-nav" ? (
            /* =========================================================================
                3A. DATE-BASED TASK NAVIGATION VIEW
               ========================================================================= */
            <div className="space-y-2.5">
              {/* Horizontal Date Scroller / Navigation Bar - All Months In Line */}
              <div className="w-full max-w-full rounded-xl shadow-xs border border-slate-200 bg-white overflow-hidden">
                <div className="px-3.5 py-2 sm:px-4 sm:py-2.5 bg-slate-50/90 border-b border-slate-200 flex flex-col md:flex-row md:items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="w-6 h-6 rounded-md bg-[#154D75] text-white flex items-center justify-center text-xs shadow-2xs shrink-0">
                      <FaCalendarDay />
                    </div>
                    <div className="min-w-0 flex items-center gap-2 flex-wrap">
                      <span className="text-xs font-bold text-slate-900 tracking-tight">
                        Task Dates
                      </span>
                      <span className="text-[11px] font-semibold text-[#154D75] bg-blue-50 border border-blue-200 px-2 py-0.5 rounded-full">
                        {taskDates.length} {taskDates.length === 1 ? "Date" : "Dates"} Available
                      </span>
                      {scrollerMonthFilter !== "ALL" && (
                        <span className="text-[10px] font-semibold text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded border border-slate-200">
                          Filtered
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Month Filter Chips + Scroll Controls */}
                  <div className="flex items-center justify-between md:justify-end gap-2 flex-wrap">
                    {/* Month Filter Pills */}
                    {uniqueTaskMonths.length > 1 && (
                      <div className="flex items-center gap-1 overflow-x-auto py-0.5 calendar-cell-scroll max-w-[280px] sm:max-w-[450px] md:max-w-[550px] lg:max-w-[700px]">
                        <button
                          type="button"
                          onClick={() => setScrollerMonthFilter("ALL")}
                          className={`px-2 py-0.5 rounded-md text-[11px] font-semibold transition-all cursor-pointer whitespace-nowrap shadow-2xs ${scrollerMonthFilter === "ALL"
                            ? "bg-[#154D75] text-white border border-[#154D75]"
                            : "bg-white text-slate-700 hover:bg-slate-100 border border-slate-300"
                            }`}
                          title="Show available dates from all months in one line"
                        >
                          All Months ({allAvailableTaskDates.length})
                        </button>
                        {uniqueTaskMonths.map((m) => (
                          <button
                            key={m.key}
                            type="button"
                            onClick={() => {
                              setScrollerMonthFilter(m.key);
                              const firstInMonth = allAvailableTaskDates.find(
                                (d) => `${d.year}-${d.monthIndex}` === m.key
                              );
                              if (firstInMonth) {
                                handleSelectTaskDate(firstInMonth);
                              }
                            }}
                            className={`px-2 py-0.5 rounded-md text-[11px] font-semibold transition-all cursor-pointer whitespace-nowrap shadow-2xs ${scrollerMonthFilter === m.key
                              ? "bg-[#154D75] text-white border border-[#154D75]"
                              : "bg-white text-slate-700 hover:bg-slate-100 border border-slate-300"
                              }`}
                            title={`Filter to ${m.label}`}
                          >
                            {m.label} ({m.dateCount})
                          </button>
                        ))}
                      </div>
                    )}

                    {/* Horizontal Scroll Quick Buttons */}
                    <div className="flex items-center gap-1 shrink-0">
                      <button
                        type="button"
                        onClick={() => scrollDatesBy(-1)}
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-semibold text-[#154D75] hover:text-white bg-white hover:bg-[#154D75] border border-slate-300 shadow-2xs transition-colors cursor-pointer"
                        title="Scroll dates left"
                      >
                        <FaChevronLeft className="text-[10px]" />
                        <span className="hidden sm:inline">Left</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => scrollDatesBy(1)}
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-semibold text-[#154D75] hover:text-white bg-white hover:bg-[#154D75] border border-slate-300 shadow-2xs transition-colors cursor-pointer"
                        title="Scroll dates right"
                      >
                        <span className="hidden sm:inline">Right</span>
                        <FaChevronRight className="text-[10px]" />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Horizontal Date Buttons Strip (All Months In Line - Compact Size) */}
                <div
                  ref={dateScrollerRef}
                  className="w-full overflow-x-auto p-2 sm:p-2.5 flex items-center gap-1.5 sm:gap-2 calendar-table-scroll touch-pan-x"
                >
                  {taskDates.length === 0 ? (
                    <div className="w-full text-center py-3 text-xs font-normal text-slate-500">
                      No task dates scheduled for {selectedParty?.CompanyName || "this party"}.
                    </div>
                  ) : (
                    (() => {
                      let lastMonthKey = null;
                      return taskDates.map((dateItem) => {
                        const currentMonthKey = `${dateItem.year}-${dateItem.monthIndex}`;
                        const isNewMonth =
                          scrollerMonthFilter === "ALL" &&
                          currentMonthKey !== lastMonthKey;
                        lastMonthKey = currentMonthKey;

                        const isSelected = selectedTaskDateKey === dateItem.dateKey;

                        return (
                          <React.Fragment key={dateItem.dateKey}>
                            {/* Month Header / Divider in the Line */}
                            {isNewMonth && (
                              <div className="flex-shrink-0 flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-slate-100 border border-slate-300 text-slate-700 shadow-2xs select-none">
                                <FaCalendarAlt className="text-[#154D75] text-[9px]" />
                                <span className="text-[10px] font-bold uppercase tracking-wide whitespace-nowrap text-slate-800">
                                  {dateItem.monthShort} {dateItem.year}
                                </span>
                              </div>
                            )}

                            <button
                              type="button"
                              data-datekey={dateItem.dateKey}
                              onClick={() => handleSelectTaskDate(dateItem)}
                              className={`group flex-shrink-0 min-w-[64px] sm:min-w-[72px] px-1.5 py-1.5 rounded-lg border text-center transition-all duration-150 cursor-pointer select-none flex flex-col items-center justify-between gap-0.5 shadow-2xs ${isSelected
                                ? "bg-[#154D75] text-white border-[#154D75] shadow-xs ring-2 ring-[#154D75]/25"
                                : "bg-white text-slate-800 border-slate-200 hover:border-[#154D75]/60 hover:bg-blue-50/40"
                                }`}
                              title={`${dateItem.fullDateStr} (${dateItem.count} ${dateItem.count === 1 ? "call" : "calls"})`}
                            >
                              {/* Top: Weekday */}
                              <span
                                className={`text-[9px] sm:text-[10px] font-bold uppercase tracking-wide leading-none ${isSelected
                                  ? "text-blue-100"
                                  : dateItem.isWeekend
                                    ? "text-rose-600"
                                    : "text-slate-500 group-hover:text-[#154D75]"
                                  }`}
                              >
                                {dateItem.dayName}
                              </span>

                              {/* Middle: Day Number + Month */}
                              <div className="flex items-baseline justify-center gap-0.5 my-0.5 leading-none">
                                <span
                                  className={`text-sm sm:text-base font-black tracking-tight ${isSelected ? "text-white" : "text-slate-900"
                                    }`}
                                >
                                  {dateItem.dayFormatted}
                                </span>
                                <span
                                  className={`text-[9px] sm:text-[10px] font-semibold uppercase ${isSelected ? "text-blue-200" : "text-slate-500"
                                    }`}
                                >
                                  {dateItem.monthShort}
                                </span>
                              </div>

                              {/* Bottom: Call count mini badge */}
                              <span
                                className={`text-[9px] font-semibold px-1.5 py-0.5 rounded-full leading-none whitespace-nowrap ${isSelected
                                  ? "bg-white/20 text-white"
                                  : "bg-slate-100 text-slate-600 border border-slate-200/80 group-hover:bg-blue-100 group-hover:text-[#154D75]"
                                  }`}
                              >
                                {dateItem.count} {dateItem.count === 1 ? "call" : "calls"}
                              </span>

                              {/* Active Indicator Bar */}
                              {isSelected && (
                                <span className="w-4 h-0.5 rounded-full bg-white mt-0.5" />
                              )}
                            </button>
                          </React.Fragment>
                        );
                      });
                    })()
                  )}
                </div>
              </div>

              {/* Tasks for Selected Date Container */}
              <div className="w-full max-w-full rounded-xl shadow-xs border border-slate-200 bg-white overflow-hidden">
                {/* Header */}
                <div className="px-3.5 py-2.5 sm:px-4 sm:py-3 bg-slate-50/90 border-b border-slate-200 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-lg bg-[#154D75] text-white flex items-center justify-center text-xs shadow-2xs shrink-0">
                      <FaCalendarCheck />
                    </div>
                    <div>
                      <h3 className="text-xs sm:text-sm font-bold text-slate-900">
                        {selectedDateObj
                          ? `Tasks for ${selectedDateObj.fullDateStr}`
                          : `Tasks for Selected Date`}
                      </h3>
                      {selectedDateObj && (
                        <p className="text-[11px] text-slate-500">
                          {selectedDateTasks.length}{" "}
                          {selectedDateTasks.length === 1 ? "task" : "tasks"} scheduled for{" "}
                          <strong className="font-semibold text-slate-700">
                            {selectedParty.CompanyName}
                          </strong>
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Prev / Next Date Quick Navigation */}
                  {taskDates.length > 1 && (
                    <div className="flex items-center gap-1.5 self-end sm:self-auto">
                      <button
                        type="button"
                        onClick={handlePrevTaskDate}
                        disabled={currentTaskDateIndex <= 0}
                        className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold border transition-colors cursor-pointer ${currentTaskDateIndex <= 0
                          ? "opacity-40 cursor-not-allowed bg-slate-100 text-slate-400 border-slate-200"
                          : "bg-white text-[#154D75] border-slate-300 hover:bg-blue-50 shadow-2xs"
                          }`}
                        title="Go to previous task date"
                      >
                        <FaChevronLeft className="text-[10px]" />
                        <span>Prev Date</span>
                      </button>
                      <button
                        type="button"
                        onClick={handleNextTaskDate}
                        disabled={
                          currentTaskDateIndex < 0 ||
                          currentTaskDateIndex >= taskDates.length - 1
                        }
                        className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold border transition-colors cursor-pointer ${currentTaskDateIndex < 0 ||
                          currentTaskDateIndex >= taskDates.length - 1
                          ? "opacity-40 cursor-not-allowed bg-slate-100 text-slate-400 border-slate-200"
                          : "bg-white text-[#154D75] border-slate-300 hover:bg-blue-50 shadow-2xs"
                          }`}
                        title="Go to next task date"
                      >
                        <span>Next Date</span>
                        <FaChevronRight className="text-[10px]" />
                      </button>
                    </div>
                  )}
                </div>

                {/* Tasks List Content */}
                <div className="p-3 sm:p-4">
                  {selectedDateTasks.length > 0 ? (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4">
                      {selectedDateTasks.map((call) => (
                        <DateNavigationTaskCard
                          key={call._id || call.CallNo}
                          call={call}
                          onClick={(clicked) => setSelectedCall(clicked)}
                        />
                      ))}
                    </div>
                  ) : (
                    <div className="p-6 sm:p-8 rounded-xl border border-dashed border-slate-300 bg-slate-50/60 text-center flex flex-col items-center justify-center">
                      <div className="w-10 h-10 rounded-full bg-slate-200 text-slate-500 flex items-center justify-center mb-2">
                        <FaInfoCircle className="text-base" />
                      </div>
                      <h4 className="text-xs sm:text-sm font-semibold text-slate-800">
                        No tasks scheduled for this date
                      </h4>
                      <p className="text-xs text-slate-500 max-w-md mt-1">
                        Select another date from the date scroller above to inspect its scheduled calls and tasks.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : (
            /* =========================================================================
                3B. 7-COLUMN CALENDAR GRID
               ========================================================================= */
            <div>
              {/* Quick Day Jump Strip on Mobile / Small Screens */}
              <div className="lg:hidden mb-1.5 px-2 py-1 bg-slate-50 border border-slate-200 rounded-xl flex items-center justify-between gap-1 shadow-2xs">
                <span className="text-xs font-medium text-[#154D75] shrink-0">
                  Jump:
                </span>
                <div className="flex items-center gap-1 overflow-x-auto py-0.5 calendar-cell-scroll">
                  {DAYS_OF_WEEK.map((day, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => scrollToDayColumn(idx)}
                      className={`px-2 py-0.5 rounded text-xs font-medium cursor-pointer transition-all shadow-2xs shrink-0 ${day.isWeekend
                        ? "bg-rose-50 text-rose-700 hover:bg-rose-100 border border-rose-200"
                        : "bg-white text-[#154D75] hover:bg-blue-50 border border-slate-200"
                        }`}
                      title={`Scroll directly to ${day.name}`}
                    >
                      {day.short}
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-0.5 shrink-0">
                  <button
                    type="button"
                    onClick={() => scrollByAmount(-1)}
                    className="p-1 rounded bg-[#154D75] text-white hover:bg-[#0e3550] cursor-pointer shadow-2xs"
                    title="Scroll Left"
                  >
                    <FaChevronLeft className="text-[10px]" />
                  </button>
                  <button
                    type="button"
                    onClick={() => scrollByAmount(1)}
                    className="p-1 rounded bg-[#154D75] text-white hover:bg-[#0e3550] cursor-pointer shadow-2xs"
                    title="Scroll Right"
                  >
                    <FaChevronRight className="text-[10px]" />
                  </button>
                </div>
              </div>

              {/* Calendar Table Wrapper with Strict Containment */}
              <div className="w-full max-w-full rounded-xl shadow-xs border border-slate-300 overflow-hidden bg-white">
                <div
                  ref={calendarScrollRef}
                  className="w-full overflow-x-auto touch-pan-x calendar-table-scroll"
                  style={{
                    WebkitOverflowScrolling: "touch",
                    overscrollBehaviorX: "contain",
                  }}
                >
                  {/* Dynamic width based on columnWidth */}
                  <div style={{ minWidth: `${columnWidth * 7}px` }} className="w-full">
                    {/* Days of week banner */}
                    <div className="grid grid-cols-7 border-b border-slate-300 text-white font-semibold text-xs">
                      {DAYS_OF_WEEK.map((day, idx) => (
                        <div
                          key={idx}
                          className={`py-2 text-center tracking-wider uppercase font-semibold text-xs ${day.isWeekend
                            ? "bg-[#dc2626]"
                            : "bg-[#154D75]"
                            } border-r border-white/20 last:border-r-0`}
                        >
                          <span className="sm:hidden">{day.short}</span>
                          <span className="hidden sm:inline">{day.name}</span>
                        </div>
                      ))}
                    </div>

                    {/* Calendar Weeks */}
                    <div className="divide-y divide-slate-200 bg-slate-200">
                      {calendarWeeks.map((week, wIdx) => {
                        const isWeekEmpty =
                          !week.hasCalls && !expandedEmptyWeeks[wIdx];

                        return (
                          <div
                            key={wIdx}
                            className={`grid grid-cols-7 divide-x divide-slate-200 transition-all ${isWeekEmpty
                              ? "min-h-[38px] sm:min-h-[44px] bg-slate-50"
                              : "min-h-[135px] sm:min-h-[165px] bg-white"
                              }`}
                          >
                            {week.days.map((dayObj, dIdx) => {
                              const hasCalls =
                                dayObj.calls && dayObj.calls.length > 0;
                              const today = isToday(dayObj);

                              if (isWeekEmpty) {
                                return (
                                  <div
                                    key={dIdx}
                                    onClick={() =>
                                      setExpandedEmptyWeeks((prev) => ({
                                        ...prev,
                                        [wIdx]: true,
                                      }))
                                    }
                                    className={`px-2 py-1.5 flex items-center justify-between transition-colors cursor-pointer ${!dayObj.isCurrentMonth
                                      ? "bg-slate-100 text-slate-400 select-none"
                                      : today
                                        ? "bg-blue-50"
                                        : "bg-slate-50 hover:bg-slate-100"
                                      }`}
                                    title={
                                      !dayObj.isCurrentMonth
                                        ? "Previous/Next Month (No calls) - Click to expand row"
                                        : `${dayObj.date.toLocaleDateString(
                                          "en-US",
                                          {
                                            month: "short",
                                            day: "numeric",
                                          }
                                        )}: No calls - Click to expand row`
                                    }
                                  >
                                    <span
                                      className={`text-xs font-semibold inline-flex items-center justify-center w-5 h-5 sm:w-6 sm:h-6 rounded-full transition-all ${!dayObj.isCurrentMonth
                                        ? "text-slate-400"
                                        : today
                                          ? "bg-[#154D75] text-white shadow-xs"
                                          : dayObj.date.getDay() === 0
                                            ? "text-[#dc2626] font-semibold"
                                            : "text-slate-800 font-semibold"
                                        }`}
                                    >
                                      {dayObj.dayNumber}
                                    </span>
                                    <span className="text-xs font-normal text-slate-400 select-none hidden sm:inline">
                                      —
                                    </span>
                                  </div>
                                );
                              }

                              // Full height week with calls (or manually expanded)
                              return (
                                <div
                                  key={dIdx}
                                  className={`min-h-[135px] sm:min-h-[165px] p-2 flex flex-col transition-colors ${!dayObj.isCurrentMonth
                                    ? "bg-slate-50 text-slate-400 select-none"
                                    : today
                                      ? "bg-blue-50/40"
                                      : "bg-white"
                                    }`}
                                >
                                  {/* Date Header inside cell */}
                                  <div className="flex items-center justify-between mb-1.5">
                                    <span
                                      className={`text-xs font-bold inline-flex items-center justify-center w-5 h-5 sm:w-6 sm:h-6 rounded-full transition-all ${!dayObj.isCurrentMonth
                                        ? "text-slate-400"
                                        : today
                                          ? "bg-[#154D75] text-white shadow-xs"
                                          : dayObj.date.getDay() === 0
                                            ? "text-[#dc2626]"
                                            : "text-[#154D75]"
                                        }`}
                                    >
                                      {dayObj.dayNumber}
                                    </span>

                                    {hasCalls ? (
                                      <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-slate-800 text-white border border-slate-800 shadow-2xs">
                                        {dayObj.calls.length}{" "}
                                        {dayObj.calls.length === 1
                                          ? "call"
                                          : "calls"}
                                      </span>
                                    ) : !week.hasCalls ? (
                                      dIdx === 6 && (
                                        <button
                                          type="button"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setExpandedEmptyWeeks((prev) => ({
                                              ...prev,
                                              [wIdx]: false,
                                            }));
                                          }}
                                          className="text-[10px] underline cursor-pointer font-medium text-slate-500 hover:text-slate-800"
                                          title="Collapse empty week"
                                        >
                                          Collapse
                                        </button>
                                      )
                                    ) : null}
                                  </div>

                                  {/* Calls List inside Date Box */}
                                  {dayObj.isCurrentMonth && (
                                    <div className="flex-1 space-y-1.5 overflow-y-auto max-h-60 pr-0.5 calendar-cell-scroll">
                                      {hasCalls
                                        ? dayObj.calls.map((call) => (
                                          <CalendarCallCard
                                            key={call._id}
                                            call={call}
                                            isExpanded={isExpandedAll}
                                            onClick={(clicked) =>
                                              setSelectedCall(clicked)
                                            }
                                          />
                                        ))
                                        : null}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>

              {/* Mobile swipe helper guide */}
              <div className="lg:hidden mt-1.5 flex items-center justify-between text-xs text-slate-500 px-1 font-normal">
                <span>◀ Sunday</span>
                <span className="text-slate-400">↔ Swipe or use buttons to view all days</span>
                <span>Saturday ▶</span>
              </div>
            </div>
          )}

          {/* =========================================================================
              4. MONTHLY SUMMARY FOOTER SECTION
             ========================================================================= */}
          <div className="w-full max-w-full rounded-xl shadow-xs border border-slate-200 overflow-hidden bg-white text-slate-800">
            <div className="p-3 sm:p-4">
              {/* Header */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1.5 sm:gap-2 mb-3 pb-2 border-b border-slate-200">
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded-lg bg-[#154D75] text-white flex items-center justify-center text-xs shadow-xs shrink-0">
                    <FaChartBar />
                  </div>
                  <div>
                    <h4 className="text-xs sm:text-sm font-semibold text-slate-900">
                      Monthly Summary &ndash; {MONTH_NAMES[selectedMonth]}{" "}
                      {selectedYear}
                    </h4>
                    <p className="text-xs font-normal text-slate-500">
                      Call statistics for {selectedParty.CompanyName}
                    </p>
                  </div>
                </div>

                <div className="flex items-center justify-between sm:justify-end gap-2">
                  <span className="text-xs font-medium text-slate-600">
                    Resolution Rate:
                  </span>
                  <span className="text-xs font-semibold px-2 py-0.5 rounded border border-emerald-200 text-emerald-800 bg-emerald-50">
                    {monthStats.total > 0
                      ? `${Math.round(
                        (monthStats.completed / monthStats.total) * 100
                      )}%`
                      : "100%"}
                  </span>
                </div>
              </div>

              {/* 4 Stats Cards */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 sm:gap-3">
                {/* 1. Total Support Calls */}
                <div className="p-3 rounded-xl border border-blue-200 shadow-2xs flex flex-col justify-between bg-blue-50/70">
                  <div className="flex items-center justify-between gap-1 mb-1">
                    <span className="text-xs font-medium uppercase tracking-wider text-slate-700 leading-tight">
                      Total Calls
                    </span>
                    <div className="w-5 h-5 rounded-md bg-[#154D75] text-white flex items-center justify-center text-xs shadow-xs shrink-0">
                      <FaPlusSquare />
                    </div>
                  </div>
                  <div className="text-xl sm:text-2xl font-bold leading-none text-[#154D75]">
                    {monthStats.total}
                  </div>
                </div>

                {/* 2. Completed */}
                <div className="p-3 rounded-xl border border-emerald-200 shadow-2xs flex flex-col justify-between bg-emerald-50/70">
                  <div className="flex items-center justify-between gap-1 mb-1">
                    <span className="text-xs font-medium uppercase tracking-wider text-emerald-800 leading-tight">
                      Completed
                    </span>
                    <div className="w-5 h-5 rounded-md bg-emerald-600 text-white flex items-center justify-center text-xs shadow-xs shrink-0">
                      <FaCheckCircle />
                    </div>
                  </div>
                  <div className="text-xl sm:text-2xl font-bold leading-none text-emerald-700">
                    {monthStats.completed}
                  </div>
                </div>

                {/* 3. In Progress */}
                <div className="p-3 rounded-xl border border-amber-200 shadow-2xs flex flex-col justify-between bg-amber-50/70">
                  <div className="flex items-center justify-between gap-1 mb-1">
                    <span className="text-xs font-medium uppercase tracking-wider text-amber-800 leading-tight">
                      In Progress
                    </span>
                    <div className="w-5 h-5 rounded-md bg-amber-500 text-white flex items-center justify-center text-xs shadow-xs shrink-0">
                      <FaClock />
                    </div>
                  </div>
                  <div className="text-xl sm:text-2xl font-bold leading-none text-amber-700">
                    {monthStats.other || 0}
                  </div>
                </div>

                {/* 4. Pending */}
                <div className="p-3 rounded-xl border border-rose-200 shadow-2xs flex flex-col justify-between bg-rose-50/70">
                  <div className="flex items-center justify-between gap-1 mb-1">
                    <span className="text-xs font-medium uppercase tracking-wider text-rose-800 leading-tight">
                      Pending
                    </span>
                    <div className="w-5 h-5 rounded-md bg-rose-600 text-white flex items-center justify-center text-xs shadow-xs shrink-0">
                      <FaTimesCircle />
                    </div>
                  </div>
                  <div className="text-xl sm:text-2xl font-bold leading-none text-rose-700">
                    {monthStats.pending}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* CALL DETAILS MODAL */}
      {selectedCall && (
        <CallDetailModal
          call={selectedCall}
          companyId={partyId}
          companyName={selectedParty?.CompanyName}
          onClose={() => setSelectedCall(null)}
        />
      )}

      {/* Off-screen Print Report Source for Direct Printing */}
      {selectedParty && (
        <div id="hidden-print-report-source" style={{ display: "none" }}>
          <PartyCallPrintReport
            party={selectedParty}
            month={selectedMonth}
            year={selectedYear}
            projectId={selectedProjectId}
            partyProjects={partyProjects}
            calendarWeeks={calendarWeeks}
            callsForMonth={callsForMonth}
            monthStats={monthStats}
            includeHistory={printIncludeHistory}
            includeImages={printIncludeImages}
            isScreenPreview={false}
          />
        </div>
      )}

      {/* PRINT CHOICE MODAL (Ask whether to print Month Report or Full Year Summary) */}
      {isChoiceModalOpen && selectedParty && (
        <PrintChoiceModal
          isOpen={isChoiceModalOpen}
          onClose={() => setIsChoiceModalOpen(false)}
          party={selectedParty}
          month={selectedMonth}
          year={selectedYear}
          initialIncludeHistory={printIncludeHistory}
          initialIncludeImages={printIncludeImages}
          onSelectReportType={handleSelectReportType}
        />
      )}

      {/* PRINT PREVIEW MODAL */}
      {isPrintPreviewOpen && selectedParty && (
        <PrintPreviewModal
          isOpen={isPrintPreviewOpen}
          onClose={() => setIsPrintPreviewOpen(false)}
          initialReportType={selectedReportType}
          party={selectedParty}
          month={selectedMonth}
          year={selectedYear}
          projectId={selectedProjectId}
          partyProjects={partyProjects}
          calendarWeeks={calendarWeeks}
          callsForMonth={callsForMonth}
          monthStats={monthStats}
          annualData={annualSummaryData}
          filteredCallsByProject={filteredCallsByProject}
          initialIncludeHistory={printIncludeHistory}
          initialIncludeImages={printIncludeImages}
        />
      )}
    </div>
  );
};

export default PartyCallCalendarView;
